import type { InternalSource } from '@jerry-serverless/analyticslib/types';
import type { FaultAccidentType, ViolationsCountType } from '@jerry-serverless/enums/user';
import type { FlowType } from '@jerry-serverless/types';

import type { Event } from '../types';
import type { VisitReason } from '../../../types/visit_reason.enum';
import type { NotInsuredReason } from '../../../types/not_insured_reason.enum';
import type { AutoInsuredLength } from '../../../types/auto_insured_length.enum';
import type { ViolationsOption } from '../../../types/signup_params';
import type { CreditRange } from '../../../types/credit_range.enum';

export const CreateAccountEventData = {
  CreateAccountStart: {
    eventAction: 'Start',
  } as Event<'New' | 'Validated' | 'Signed' | 'Returning'>,
  CreateAccountMALContinue: {
    eventAction: 'MyAutoLoan',
    eventLabel: 'Continue',
  } as Event<string>,
  CreateAccountServiceSplitPageEnter: {
    eventAction: 'EnterServiceSplitPage',
  } as Event<''>,
  CreateAccountServiceSelected: {
    eventAction: 'ServiceSelected',
  } as Event<string>,
  CreateAccountShopFor: {
    eventAction: 'ShopFor',
  } as Event<''>,
  CreateAccountShopForOrigin: {
    eventAction: 'ShopForOrigin',
  } as Event<InternalSource>,
  CreateAccountInsured: {
    eventAction: 'isAutoInsured',
  } as Event<'Yes' | 'No'>,
  CreateAccountInsuredByHistory: {
    eventAction: 'isAutoInsuredByHistory',
  } as Event<'Yes' | 'No'>,
  CreateAccountAutoInsuredLength: {
    eventAction: 'AutoInsuredLength',
  } as Event<AutoInsuredLength>,
  CreateAccountNotInsuredReason: {
    eventAction: 'NotInsuredReason',
  } as Event<NotInsuredReason>,
  CreateContinuousCoverageLength: {
    eventAction: 'continuousCoverageLength',
  } as Event<string>,
  CreateAccountClaims: {
    eventAction: 'Claims',
  } as Event<'Yes' | 'No'>,
  CreateAccountViolations: {
    eventAction: 'Violations',
  } as Event<ViolationsOption>,
  CreateAccountVisitReason: {
    eventAction: 'Visit Reason',
  } as Event<VisitReason>,
  CreateAccountName: {
    eventAction: 'Name',
  } as Event<''>,
  CreateAccountSelectNameVariation: {
    eventAction: 'Name Variation Select',
  } as Event<string>,
  CreateAccountEnterNameVariation: {
    eventAction: 'Name Variation Manual Entry',
  } as Event<''>,
  CreateAccountPhone: {
    eventAction: 'Phone',
  } as Event<string>,
  CreateAccountPhoneSubmitted: {
    eventAction: 'Phone Submitted',
  } as Event<''>,
  CreateAccountPhoneChanged: {
    eventAction: 'Phone Changed',
  } as Event<string>,
  CreateAccountPhoneAlreadyRegistered: {
    eventAction: 'Phone',
    eventLabel: 'Number already registered',
  } as Event<'Number already registered'>,
  CreateAccountPhoneCodeSubmitted: {
    eventAction: 'Phone Code Submitted',
  } as Event<''>,
  CreateAccountResendCodeSubmitted: {
    eventAction: 'Resend Code Submitted',
  } as Event<''>,
  CreateAccountResendCodeByCallSubmitted: {
    eventAction: 'Resend Code By Call Submitted',
  } as Event<''>,
  CreateAccountPhoneCodeAndAuthorize: {
    eventAction: 'Phone Code & Authorize',
  } as Event<string>,
  CreateAccountPhoneResponseDataAbnormal: {
    eventAction: 'Phone Code Submitted & Response data abnormal',
  } as Event<string>,
  CreateAccountPartner: {
    eventAction: 'Partner source',
  } as Event<string>,
  CreateAccountPartnerLanding: {
    eventAction: 'Partner Landing',
  } as Event<string>,
  CreateAccountMaintenanceReminderPromptPageEnter: {
    eventAction: 'EnterMaintenanceReminderPromptPage',
  } as Event<''>,
  CreateAccountMaintenanceReminderPromptPageSkip: {
    eventAction: 'EnterMaintenanceReminderPromptPage - Skip',
  } as Event<''>,
  CreateAccountMaintenanceReminderCarSelected: {
    eventAction: 'MaintenanceReminderPromptPage - CarSelected',
  } as Event<''>,
  CreateAccountRecallAlertsSelection: {
    eventAction: 'RecallAlertsSelection',
  } as Event<'Yes' | 'No'>,
  CreateAccountAddMaintenanceReminderOilChangeLastCompletedTime: {
    eventAction: 'SetupReminderFlow - OilChangeSetupFlow',
  } as Event<''>,
  CreateAccountAddMaintenanceReminderOilType: {
    eventAction: 'SetupReminderFlow - OilTypeSetupFlow',
  } as Event<''>,
  CreateAccountAddMaintenanceReminderTireRotatedLastCompletedTime: {
    eventAction: 'SetupReminderFlow - TireRotationSetupFlow',
  } as Event<''>,
  CreateAccountAddMaintenanceReminderBrakePadsLastCompletedTime: {
    eventAction: 'SetupReminderFlow - BrakePadsSetupFlow',
  } as Event<''>,
  CreateAccountCarLoanBalance: {
    eventAction: 'Loan Balance',
  } as Event<string>,
  CreateAccountCarLoanPayment: {
    eventAction: 'Loan Payment',
  } as Event<string>,
  CreateAccountCarMinPayment: {
    eventAction: 'Min number of payments',
  } as Event<string>,
  CreateAccountIncome: {
    eventAction: 'Income',
  } as Event<''>,
  CreateAccountSetHomeOwnership: {
    eventAction: 'HomeOwnership',
  } as Event<string>,
  CreateAccountSetCreditRange: {
    eventAction: 'CreditRange',
  } as Event<CreditRange>,
  ShopforLowestPrice: {
    eventAction: 'ShopforLowestPrice',
  } as Event<'Viewed' | 'Requested'>,
  WelcomeScreen: {
    eventAction: 'WelcomeScreen',
  } as Event<string>,
  CreateAccountIntroDriveCount: {
    eventAction: 'IntroDriveCount',
  } as Event<'Viewed' | 'Selected'>,
  CreateAccountIntroGoodDriver: {
    eventAction: 'IntroGoodDriver',
  } as Event<'Viewed' | 'Selected'>,
  LoginChallengeButtonClicked: {
    eventAction: 'LoginChallengeButtonClicked',
  } as Event<'I have the account' | 'Create a new account' | 'I dont remember'>,
  LoginChallengeDOBVerified: {
    eventAction: 'LoginChallengeDOBVerified',
  } as Event<'Success' | 'Failure'>,
  SignExistingAccount: {
    eventAction: 'SignExistingAccount',
  } as Event<string>,
  LoginChallengeStart: {
    eventAction: 'LoginChallengeStart',
  } as Event<'Matched' | 'NotMatched'>,
  RecallDataFetched: {
    eventAction: 'RecallDataFetched',
  } as Event<FlowType>,
  PrefillQuotingCreateUser: {
    eventAction: 'PrefillQuotingCreateUser',
  } as Event<string>,
  PrefillQuotingInterstitial: {
    eventAction: 'PrefillQuotingInterstitial',
  } as Event<string>,
  PrefillQuotingSendCode: {
    eventAction: 'PrefillQuotingSendCode',
  } as Event<string>,
  PrefillQuotingConfirmInformation: {
    eventAction: 'PrefillQuotingConfirmInformation',
  } as Event<string>,
  ConfirmInformation: {
    eventAction: 'ConfirmInformation',
  } as Event<string>,
  UpdateDobSuccessContinue: {
    eventAction: 'UpdateDobSuccessContinue',
  } as Event<string>,
  UpdateDobFailureRetry: {
    eventAction: 'UpdateDobFailureRetry',
  } as Event<string>,
  UpdateDobFailureCreateNewAccount: {
    eventAction: 'UpdateDobFailureCreateNewAccount',
  } as Event<string>,
  FaultAccident: {
    eventAction: 'FaultAccidentType',
  } as Event<FaultAccidentType>,
  ViolationsCount: {
    eventAction: 'ViolationsCount',
  } as Event<ViolationsCountType>,
  GarageOnlyStart: {
    eventAction: 'GarageOnlyStart',
  } as Event<string>,
  GarageOnlyAPI: {
    eventAction: 'GarageOnlyAPI',
  } as Event<string>,
  GarageOnlySkipContinue: {
    eventAction: 'GarageOnlySkipContinue',
  } as Event<string>,
  GarageOnlyRepairJobs: {
    eventAction: 'GarageOnlyRepairJobs',
  } as Event<string>,
};
